import * as ucans from '@ucans/ucans';
import ReconnectingWebSocket, { UrlProvider, CloseEvent } from 'partysocket/ws';
import { uuid } from 'short-uuid';
import {
  ConnectHandler,
  DisconnectHandler,
  ISocketInitConfig,
  EventHandler,
  RequestPayload,
  SequenceResponseCB,
  SequenceToRequestMap,
  SequenceToRequestMapValue,
  SocketStatusEnum,
  SendUpdateResponse,
  FetchDocUpdatesResponse,
  SendUpdateParams,
  SetOnchainFileIdResponse,
  DeleteUpdateResponse,
  AuthResponse,
  SetKeyResponse,
} from '@/types';
import { CHAIN_ID } from '@/utils/constants';
import { captureException } from '@sentry/nextjs';

export const WEBSOCKET_CONFIG = {
  maxReconnectionDelay: 10000,
  minReconnectionDelay: 1500,
  reconnectionDelayGrowFactor: 1.3,
  connectionTimeout: 6000,
  maxRetries: 0,
};

export class SocketClient {
  private _websocketUrl: UrlProvider;
  private _machineEventHandler: EventHandler | null = null;
  private _onConnect: ConnectHandler | null = null;
  private _onDisconnection: DisconnectHandler | null = null;
  private _sequenceCallbackMap: SequenceToRequestMap = {};
  _webSocketStatus: SocketStatusEnum = SocketStatusEnum.CLOSED;
  private _webSocket: ReconnectingWebSocket | null = null;
  private _websocketServiceDid = '';
  private chainId = CHAIN_ID;
  private clientUsername = '';
  private didSecret = '';
  private documentId = '';
  _onError: ISocketInitConfig['onError'] | null = null;
  roomId = '';

  constructor(url: UrlProvider) {
    this._websocketUrl = url;
    this._processMessage = this._processMessage.bind(this);
  }

  private _getSequenceIdCallback(id: string): SequenceToRequestMapValue {
    return this._sequenceCallbackMap[id];
  }

  private _removeSequenceIdFromMap(id: string) {
    delete this._sequenceCallbackMap[id];
  }

  private _registerSequenceCallback(
    seq: string,
    callback: SequenceResponseCB
  ): void {
    this._sequenceCallbackMap[seq] = { callback };
  }

  private async _sendNetworkRequest(
    data: string,
    seqId?: string
  ): Promise<any> {
    if (
      this._webSocketStatus !== SocketStatusEnum.CONNECTED ||
      !this._webSocket
    ) {
      throw new Error(
        'cannot make network request, websocket is not connected'
      );
    }

    return new Promise((resolve) => {
      if (!this._webSocket) {
        resolve(null);
        return;
      }
      this._webSocket.send(data);
      if (seqId) {
        this._registerSequenceCallback(seqId, resolve);
      } else {
        resolve(null);
      }
    });
  }

  public async sendUpdate({
    last_published_content_hash,
    data,
  }: SendUpdateParams) {
    const args = {
      data,
      last_published_content_hash,
    };
    const response: SendUpdateResponse = await this._buildRequest(
      '/ddocs/documents/update',
      args
    );

    return response;
  }
  public async deleteUpdates(
    update_ids: string[],
    latest_published_content_hash: string | null
  ) {
    const args = {
      update_ids,
      latest_published_content_hash,
    };
    const response: DeleteUpdateResponse = await this._buildRequest(
      '/ddocs/documents/commit',
      args
    );
    return response;
  }

  async getUncommittedChanges() {
    const args = {
      limit: 1000,
      offset: 0,
    };
    const response = await this._buildRequest<FetchDocUpdatesResponse>(
      '/ddocs/documents/update/history',
      args
    );
    return response;
  }

  public disconnect = (reason: string) => {
    if (!this._webSocket) return;
    this._webSocketStatus = SocketStatusEnum.DISCONNECTING;
    this._webSocket.onopen = null;
    this._webSocket.removeEventListener('message', this._processMessage);
    this._webSocket.close(1000, reason);
    this._webSocketStatus = SocketStatusEnum.DISCONNECTED;
  };

  private async _buildRequest<T>(cmd: string, args: any) {
    if (!this.documentId) {
      throw new Error(
        `Cannot perform action without room id: ${this.documentId}`
      );
    }

    const seqId = uuid();
    const req: RequestPayload = {
      cmd,
      args: {
        document_id: this.documentId,
        ...args,
      },
      seq_id: seqId,
    };
    return (await this._sendNetworkRequest(JSON.stringify(req), seqId)) as T;
  }

  private getKeyPair() {
    if (!this.didSecret) {
      throw new Error('owner DID is missing');
    }
    return ucans.EdKeypair.fromSecretKey(this.didSecret);
  }

  private buildToken = async () => {
    if (!this._websocketServiceDid) {
      throw new Error('Server did not response with the server DID');
    }
    const keyPair = this.getKeyPair();
    const token_ucan = await ucans.build({
      audience: this._websocketServiceDid,
      issuer: keyPair,
      capabilities: [
        {
          with: {
            scheme: 'sync',
            hierPart: this.documentId,
          },
          can: { namespace: 'file', segments: ['EDIT'] },
        },
      ],
      expiration: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 7, // Expires in 7 days
    });
    const token = ucans.encode(token_ucan);
    return token;
  };

  private _handleHandShake = async (message: any) => {
    this._websocketServiceDid = message.server_did;
    if (
      this._webSocketStatus !== SocketStatusEnum.CONNECTED ||
      !this.documentId
    ) {
      throw new Error(
        'Cannot establish handshake. WebSocket not connected or roomId not defined'
      );
    }
    if (!this.clientUsername) {
      throw new Error('User name is required to establish handshake');
    }
    const token = await this.buildToken();
    const seqId = uuid();
    const req: RequestPayload = {
      cmd: '/ddocs/auth',
      args: {
        invoker_address: this.clientUsername,
        token: token,
      },
      seq_id: seqId,
    };
    const response: AuthResponse = await this._sendNetworkRequest(
      JSON.stringify(req),
      seqId
    );
    if (!response.is_handshake_response) {
      this.disconnect('Request Malformed, response is is not for handshake');
      return;
    }
    const { document_key, on_chain_document_id } = response.data;
    this._onConnect?.({
      document_key,
      on_chain_document_id,
      roomId: this.roomId,
    });
  };

  private _executeRequestCallback = (data: any) => {
    const callbackMap = this._getSequenceIdCallback(data.seq_id);

    if (callbackMap && typeof callbackMap.callback === 'function') {
      this._removeSequenceIdFromMap(data.seq_id);
      delete data.seq_id;
      callbackMap.callback(data);
      if (data.err) {
        captureException(data.err);
        this._onError?.(data.err);
        return;
      }
      return;
    }
  };

  private async _processMessage(event: MessageEvent) {
    if (!event.data) throw new Error('Failed to get message data');
    const message = JSON.parse(event.data);
    if (message.seq_id) {
      this._executeRequestCallback(message);
      return;
    }
    if (message.is_handshake_response) {
      this._handleHandShake(message);
      return;
    }
    if (message.event) {
      this._machineEventHandler?.(message);
    }
  }

  private _clearSequenceCallbackMap() {
    this._sequenceCallbackMap = {};
  }

  public connectSocket() {
    if (
      this._webSocketStatus === SocketStatusEnum.CONNECTED ||
      this._webSocketStatus === SocketStatusEnum.CONNECTING
    ) {
      return;
    }

    this._webSocketStatus = SocketStatusEnum.CONNECTING;

    return new Promise<void>((resolve) => {
      this._webSocket = new ReconnectingWebSocket(
        this._websocketUrl,
        [],
        WEBSOCKET_CONFIG
      );
      this._webSocket.addEventListener('message', this._processMessage);
      this._webSocket.onopen = () => {
        this._webSocketStatus = SocketStatusEnum.CONNECTED;
        this._clearSequenceCallbackMap();
        resolve();
      };
      this._webSocket.onclose = (e: CloseEvent) => {
        this._webSocketStatus = SocketStatusEnum.CLOSED;
        this._onDisconnection?.(e);
      };
      this._webSocket.onerror = (e) => {
        captureException(e);
        this._webSocketStatus = SocketStatusEnum.CLOSED;
        this._onError?.('Failed to connect to Socket');
      };
    });
  }

  public setDocumentFileId = async (id: number) => {
    const args = {
      on_chain_document_id: id,
    };
    const response: SetOnchainFileIdResponse = await this._buildRequest(
      '/ddocs/documents/set-published',
      args
    );

    return response;
  };
  public setDocumentKey = async (key: string) => {
    const args = {
      document_key: key,
    };

    const response: SetKeyResponse = await this._buildRequest(
      '/ddocs/documents/set-key',
      args
    );

    return response;
  };

  public async init(config: ISocketInitConfig) {
    this._onConnect = config.onConnect;
    this._onDisconnection = config.onDisconnect;
    this._machineEventHandler = config.onWsEvent;
    this._onError = config.onError;
    this.clientUsername = config.username;
    this.didSecret = config.didSecret;
    this.documentId = `${this.chainId}/${config.portalAddress}/${config.roomId}`;
    this.roomId = config.roomId;
    await this.connectSocket();
  }
}
